import {
  Button,
  DatePicker,
  Input,
  Modal,
  Select,
  UploadFile,
} from "antd";
import { ReactNode, useEffect, useState } from "react";
import styled from "styled-components";
import { useAtomValue } from "jotai";
import dayjs from "dayjs";
import { releaseYearValue } from "../../utils/year";
import _ from "lodash";
import { v4 } from "uuid";
import { Subject } from "rxjs";
import {
  MdAdd,
  MdClose,
  MdEmojiEvents,
  MdEvent,
  MdImage,
  MdInfoOutline,
  MdLiveTv,
  MdLocationOn,
  MdMilitaryTech,
  MdPhotoLibrary,
  MdPublic,
  MdSubject,
  MdTitle,
  MdTv,
} from "react-icons/md";
import { masterToOptions } from "../../../../utils/functions/misc/masterToOptions";
import { masterAtom } from "../../../../atoms/masters.atom";
import { countries } from "../../../../data/countries";
import { Season } from "../../../../models/season.model";
import { StatePair } from "../../../../types/SetState";
import { TextAreaResize } from "../../../../components/ui/textarea-resize/textarea-resize.component";
import { ImageUploader } from "../../../../components/ui/image-uploader/image-uploader.component";
import { replacePoster, samePosterUrl } from "../../utils/posters";
import { colors } from "../../../../theme/colors";
import {
  Field,
  FieldGrid,
  HelperRow,
  Section,
  card,
} from "../../../films/components/section-card.component";

const SYNOPSIS_LIMIT = 1500;

export const StepFour = ({
  seasons,
  setSeasons,
  activeSeasonIndex,
  setActiveSeasonIndex,
  show = true,
  stepFourTab,
  setStepFourTab,
  stepFourShowErrors,
}: {
  show?: boolean;
  nameOptions: {
    value: string;
    label: string;
    logo?: string;
    id?: string;
    full_name?: string;
    custom_url?: string;
    type?: "professional" | "business";
  }[];
  professionalProfileSubject$: Subject<string>;
  stepFourTab: 0 | 1;
  setStepFourTab: (tab: 0 | 1) => void;
  stepFourShowErrors: boolean;
} & StatePair<"seasons", "setSeasons", Season[]> &
  StatePair<"activeSeasonIndex", "setActiveSeasonIndex", number>) => {
  const master = useAtomValue(masterAtom);

  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [seasonToDelete, setSeasonToDelete] = useState<number | null>(null);

  useEffect(() => {
    if (seasons.length === 0) {
      setSeasons([
        {
          id: v4(),
          season_number: 1,
          season_title: "",
          season_synopsis: "",
          release_status: "unreleased",
          episodes: [],
          awards: [],
          horizontal_poster: [],
          vertical_poster: [],
        },
      ]);
    }
  }, [seasons, setSeasons]);

  const activeSeason = seasons[activeSeasonIndex] ?? ({} as Season);
  const synopsisCount = activeSeason.season_synopsis?.length ?? 0;
  const missingTitle = !activeSeason?.season_title;
  const missingSynopsis = !activeSeason?.season_synopsis;
  const missingReleaseStatus = !activeSeason?.release_status;
  const isReleased = activeSeason?.release_status === "released";
  const selectedReleaseYear = releaseYearValue(activeSeason?.year_of_release);
  const missingYear = isReleased && !selectedReleaseYear;
  const missingMedium = isReleased && !activeSeason?.medium_of_release;
  const missingPlatform = isReleased && !activeSeason?.release_platform;
  const missingVerticalPoster =
    !(activeSeason?.vertical_poster?.length > 0);
  const missingHorizontalPoster =
    !(activeSeason?.horizontal_poster?.length > 0);

  const mutateActive = (mutator: (next: Season) => void) => {
    const next = [...seasons];
    next[activeSeasonIndex] = { ...next[activeSeasonIndex], modified: true };
    mutator(next[activeSeasonIndex]);
    setSeasons(next);
  };

  const seasonTitle =
    activeSeason?.season_title ||
    `Season ${activeSeasonIndex + 1}`;

  const updateVerticalAt = (type: 0 | 1 | 2) => ({
    url: (activeSeason?.vertical_poster ?? [])
      .filter((p) => p.type === type)
      .map((p) => p.url ?? ""),
    onRemove: (file: UploadFile) => {
      mutateActive((s) => {
        s.vertical_poster = (s.vertical_poster ?? []).filter(
          (poster) => !(poster.type === type && samePosterUrl(poster.url, file.url))
        );
      });
    },
    onComplete: (urls: UploadFile[]) => {
      mutateActive((s) => {
        s.vertical_poster = replacePoster(s.vertical_poster, type, urls.at(-1));
      });
    },
  });

  const updateHorizontalAt = (type: 0 | 1 | 2) => ({
    url: (activeSeason?.horizontal_poster ?? [])
      .filter((p) => p.type === type)
      .map((p) => p.url ?? ""),
    onRemove: (file: UploadFile) => {
      mutateActive((s) => {
        s.horizontal_poster = (s.horizontal_poster ?? []).filter(
          (poster) => !(poster.type === type && samePosterUrl(poster.url, file.url))
        );
      });
    },
    onComplete: (urls: UploadFile[]) => {
      mutateActive((s) => {
        s.horizontal_poster = replacePoster(s.horizontal_poster, type, urls.at(-1));
      });
    },
  });

  const addSeason = () => {
    setSeasons((prev) => [
      ...prev,
      {
        id: v4(),
        season_number:
          prev.reduce((max, season) => Math.max(max, season.season_number || 0), 0) + 1,
        modified: true,
        season_title: "",
        season_synopsis: "",
        release_status: "unreleased",
        episodes: [
          {
            title: "",
            duration: "0:0",
            episode_number: 1,
            release_date: "",
            cast: [],
            crew: [],
            images: [],
            vertical_poster: [],
            horizontal_poster: [],
            synopsis: "",
            shooting_format: "",
            aspect_ratio: "",
            id: v4(),
            ratings: {
              censor: "",
              adultContent: "",
              language: "",
              violence: "",
              drugs: "",
              harmful: "",
              hateful: "",
            },
            awards: [],
          },
        ],
        awards: [],
        horizontal_poster: [],
        vertical_poster: [],
      },
    ]);
    setActiveSeasonIndex(seasons.length);
  };

  const confirmDeleteSeason = () => {
    if (seasonToDelete !== null) {
      const next = [...seasons];
      next.splice(seasonToDelete, 1);
      setSeasons(next);
      if (activeSeasonIndex === seasonToDelete) {
        setActiveSeasonIndex(Math.max(0, seasonToDelete - 1));
      } else if (activeSeasonIndex > seasonToDelete) {
        setActiveSeasonIndex(activeSeasonIndex - 1);
      }
    }
    setDeleteConfirmVisible(false);
    setSeasonToDelete(null);
  };

  return (
    <Wrapper style={{ display: show ? "flex" : "none" }}>
      <SeasonBar>
        <SeasonBarHead>
          <SeasonBarTitle>Seasons</SeasonBarTitle>
          <SeasonBarHint>
            Switch between seasons to edit their details.
          </SeasonBarHint>
        </SeasonBarHead>
        <SeasonChips>
          {seasons.map((season, index) => (
            <SeasonChip
              key={season.id ?? `season-${index}`}
              $active={activeSeasonIndex === index}
              onClick={() => setActiveSeasonIndex(index)}
            >
              <ChipNumber>{index + 1}</ChipNumber>
              <ChipLabel>
                {season.season_title || `Season ${index + 1}`}
              </ChipLabel>
              {seasons.length > 1 ? (
                <ChipRemove
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSeasonToDelete(index);
                    setDeleteConfirmVisible(true);
                  }}
                  aria-label="Remove season"
                >
                  <MdClose />
                </ChipRemove>
              ) : null}
            </SeasonChip>
          ))}
          <AddSeasonChip type="button" onClick={addSeason}>
            <MdAdd />
            <span>Add season</span>
          </AddSeasonChip>
        </SeasonChips>
      </SeasonBar>

      <SegmentedTabs role="tablist">
        <SegmentedTab
          type="button"
          role="tab"
          $active={stepFourTab === 0}
          aria-selected={stepFourTab === 0}
          onClick={() => setStepFourTab(0)}
        >
          <MdInfoOutline />
          Season Details
        </SegmentedTab>
        <SegmentedTab
          type="button"
          role="tab"
          $active={stepFourTab === 1}
          aria-selected={stepFourTab === 1}
          onClick={() => setStepFourTab(1)}
        >
          <MdEmojiEvents />
          Awards & Festivals
        </SegmentedTab>
      </SegmentedTabs>

      {stepFourTab === 0 ? (
        <>
          <Section
            icon={<MdInfoOutline />}
            title={`${seasonTitle} · Details`}
            subtitle="Title, synopsis and release status for this season."
          >
            <FieldGrid $cols={3}>
              <Field
                label="Season Title"
                icon={<MdTitle />}
                required
                helper={
                  stepFourShowErrors && missingTitle
                    ? undefined
                    : "What's this season called?"
                }
              >
                <Input
                  size="large"
                  placeholder={`Season ${activeSeasonIndex + 1}`}
                  value={activeSeason?.season_title ?? ""}
                  status={
                    stepFourShowErrors && missingTitle ? "error" : undefined
                  }
                  onChange={(e) =>
                    mutateActive((s) => {
                      s.season_title = e.target.value;
                    })
                  }
                />
                {stepFourShowErrors && missingTitle ? (
                  <FieldError>Please enter a season title.</FieldError>
                ) : null}
              </Field>
              <Field
                label="Release Status"
                icon={<MdLiveTv />}
                required
                helper={
                  stepFourShowErrors && missingReleaseStatus
                    ? undefined
                    : "Has this season released yet?"
                }
              >
                <Select
                  size="large"
                  placeholder="Select release status"
                  status={
                    stepFourShowErrors && missingReleaseStatus
                      ? "error"
                      : undefined
                  }
                  options={[
                    { label: "Released", value: "released" },
                    { label: "Unreleased", value: "unreleased" },
                    { label: "Under Production", value: "under_production" },
                  ]}
                  value={activeSeason?.release_status ?? undefined}
                  onSelect={(status) =>
                    mutateActive((s) => {
                      s.release_status = status;
                    })
                  }
                />
                {stepFourShowErrors && missingReleaseStatus ? (
                  <FieldError>Please select a release status.</FieldError>
                ) : null}
              </Field>
              {isReleased ? (
                <Field
                  label="Year of First Release"
                  icon={<MdEvent />}
                  required
                  helper={
                    stepFourShowErrors && missingYear
                      ? undefined
                      : "When did this season first air?"
                  }
                >
                  <DatePicker
                    size="large"
                    style={{ width: "100%" }}
                    picker="year"
                    format="YYYY"
                    placeholder="Select year"
                    value={selectedReleaseYear}
                    status={
                      stepFourShowErrors && missingYear ? "error" : undefined
                    }
                    disabledDate={(current) =>
                      current && current > dayjs().endOf("year")
                    }
                    onChange={(date) =>
                      mutateActive((s) => {
                        s.year_of_release =
                          date && date.isValid() ? date : undefined;
                      })
                    }
                  />
                  {stepFourShowErrors && missingYear ? (
                    <FieldError>Please select a year of first release.</FieldError>
                  ) : null}
                </Field>
              ) : null}
              {isReleased ? (
                <Field
                  label="Medium of Release"
                  icon={<MdTv />}
                  required
                  helper={
                    stepFourShowErrors && missingMedium
                      ? undefined
                      : "Where did the season air?"
                  }
                >
                  <Select
                    size="large"
                    placeholder="Select medium"
                    status={
                      stepFourShowErrors && missingMedium
                        ? "error"
                        : undefined
                    }
                    options={[
                      { label: "TV", value: "TV" },
                      { label: "Web", value: "web" },
                      { label: "Mobile", value: "mobile" },
                      { label: "Other", value: "other" },
                    ]}
                    value={activeSeason?.medium_of_release ?? undefined}
                    onSelect={(medium) =>
                      mutateActive((s) => {
                        s.medium_of_release = medium;
                      })
                    }
                  />
                  {stepFourShowErrors && missingMedium ? (
                    <FieldError>Please select a medium of release.</FieldError>
                  ) : null}
                </Field>
              ) : null}
              {isReleased ? (
                <Field
                  label="Release Platform"
                  icon={<MdLocationOn />}
                  required
                  helper={
                    stepFourShowErrors && missingPlatform
                      ? undefined
                      : "Channel, OTT, or platform name."
                  }
                >
                  <Input
                    size="large"
                    placeholder="e.g. Netflix"
                    value={activeSeason?.release_platform ?? ""}
                    status={
                      stepFourShowErrors && missingPlatform
                        ? "error"
                        : undefined
                    }
                    onChange={(e) =>
                      mutateActive((s) => {
                        s.release_platform = e.target.value;
                      })
                    }
                  />
                  {stepFourShowErrors && missingPlatform ? (
                    <FieldError>Please enter a release platform.</FieldError>
                  ) : null}
                </Field>
              ) : null}
              <Field
                label="Synopsis"
                icon={<MdSubject />}
                required
                helper={
                  <HelperRow>
                    <span>What is this season about?</span>
                    <span>
                      {synopsisCount} / {SYNOPSIS_LIMIT}
                    </span>
                  </HelperRow>
                }
                fullWidth
              >
                <TextAreaResize
                  value={activeSeason?.season_synopsis ?? ""}
                  placeholder="Tell us what this season is about…"
                  rows={4}
                  maxLength={SYNOPSIS_LIMIT}
                  onChange={(str) =>
                    mutateActive((s) => {
                      s.season_synopsis = str.toString();
                    })
                  }
                />
                {stepFourShowErrors && missingSynopsis ? (
                  <FieldError>Please enter a synopsis.</FieldError>
                ) : null}
              </Field>
            </FieldGrid>
          </Section>

          <Section
            icon={<MdImage />}
            title={`${seasonTitle} · Posters`}
            subtitle="Artwork specific to this season. JPG or PNG, up to 10MB each."
          >
            <SubHead>Vertical posters</SubHead>
            <PosterGrid>
              <UploadField
                label="Vertical Poster"
                aspect="3:4"
                help="At least one vertical poster is required."
                required
                error={
                  stepFourShowErrors && missingVerticalPoster
                    ? "Please upload at least one vertical poster"
                    : ""
                }
              >
                <ImageUploader
                  outputAsJpeg
                  maxCount={2}
                  aspect={3 / 4}
                  name="vertical_poster"
                  action=""
                  placeholder="Click or drag"
                  hint="3:4 · max 2"
                  {...updateVerticalAt(0)}
                />
              </UploadField>
              <UploadField
                label="Vertical Poster"
                aspect="4:5"
                help="Optional."
              >
                <ImageUploader
                  outputAsJpeg
                  maxCount={2}
                  aspect={4 / 5}
                  name="vertical_poster"
                  action=""
                  placeholder="Click or drag"
                  hint="4:5 · max 2"
                  {...updateVerticalAt(1)}
                />
              </UploadField>
              <UploadField
                label="Vertical Poster"
                aspect="1:1"
                help="Optional."
              >
                <ImageUploader
                  outputAsJpeg
                  maxCount={2}
                  aspect={1 / 1}
                  name="vertical_poster"
                  action=""
                  placeholder="Click or drag"
                  hint="1:1 · max 2"
                  {...updateVerticalAt(2)}
                />
              </UploadField>
            </PosterGrid>

            <Divider />

            <SubHead>Horizontal posters</SubHead>
            <PosterGrid>
              <UploadField
                label="Horizontal Poster"
                aspect="16:9"
                help="At least one horizontal poster is required."
                required
                error={
                  stepFourShowErrors && missingHorizontalPoster
                    ? "Please upload at least one horizontal poster"
                    : ""
                }
              >
                <ImageUploader
                  outputAsJpeg
                  maxCount={2}
                  aspect={16 / 9}
                  name="horizontal_poster"
                  action=""
                  placeholder="Click or drag"
                  hint="16:9 · max 2"
                  {...updateHorizontalAt(0)}
                />
              </UploadField>
              <UploadField
                label="Horizontal Poster"
                aspect="4:3"
                help="Optional."
              >
                <ImageUploader
                  outputAsJpeg
                  maxCount={2}
                  aspect={4 / 3}
                  name="horizontal_poster"
                  action=""
                  placeholder="Click or drag"
                  hint="4:3 · max 2"
                  {...updateHorizontalAt(1)}
                />
              </UploadField>
              <UploadField
                label="Horizontal Poster"
                aspect="2.39:1"
                help="Optional."
              >
                <ImageUploader
                  outputAsJpeg
                  maxCount={2}
                  aspect={2.39 / 1}
                  name="horizontal_poster"
                  action=""
                  placeholder="Click or drag"
                  hint="2.39:1 · max 2"
                  {...updateHorizontalAt(2)}
                />
              </UploadField>
            </PosterGrid>
          </Section>
        </>
      ) : (
        <Section
          icon={<MdEmojiEvents />}
          title={`${seasonTitle} · Awards & Festivals`}
          subtitle="Add the festivals this season has played at and the awards it has earned. This step is optional."
        >
          {(activeSeason?.awards ?? []).length === 0 ? (
            <EmptyState>
              <EmptyIcon>
                <MdMilitaryTech />
              </EmptyIcon>
              <EmptyTitle>No awards added yet</EmptyTitle>
              <EmptyHint>
                Showcase the festivals and honors this season has received.
              </EmptyHint>
            </EmptyState>
          ) : (
            <AwardsList>
              {(activeSeason?.awards ?? []).map((award, index) => (
                <AwardCard key={index}>
                  <AwardHead>
                    <AwardHeadGrow>
                      <Input
                        size="large"
                        placeholder="Festival name"
                        value={award.name_of_festival ?? ""}
                        onChange={(e) => {
                          const next = _.cloneDeep(seasons);
                          next[activeSeasonIndex].awards[
                            index
                          ].name_of_festival = e.target.value;
                          next[activeSeasonIndex].modified = true;
                          setSeasons(next);
                        }}
                      />
                    </AwardHeadGrow>
                    <AwardIconBtn
                      type="button"
                      $danger
                      onClick={() => {
                        const next = _.cloneDeep(seasons);
                        next[activeSeasonIndex].awards.splice(index, 1);
                        next[activeSeasonIndex].modified = true;
                        setSeasons(next);
                      }}
                      aria-label="Remove award"
                    >
                      <MdClose />
                    </AwardIconBtn>
                  </AwardHead>
                  <AwardGrid>
                    <AwardField label="Country" icon={<MdPublic />}>
                      <Select
                        size="large"
                        showSearch
                        allowClear
                        placeholder="Select country"
                        style={{ width: "100%" }}
                        options={countries}
                        value={award.country ?? undefined}
                        onChange={(value) => {
                          const next = _.cloneDeep(seasons);
                          next[activeSeasonIndex].awards[index].country =
                            value;
                          next[activeSeasonIndex].modified = true;
                          setSeasons(next);
                        }}
                      />
                    </AwardField>
                    <AwardField label="Year" icon={<MdEvent />}>
                      <DatePicker
                        size="large"
                        picker="year"
                        format="YYYY"
                        placeholder="Select year"
                        style={{ width: "100%" }}
                        value={releaseYearValue(award.award_year)}
                        onChange={(date) => {
                          const next = _.cloneDeep(seasons);
                          next[activeSeasonIndex].awards[index].award_year =
                            date;
                          next[activeSeasonIndex].modified = true;
                          setSeasons(next);
                        }}
                      />
                    </AwardField>
                    <AwardField label="Category" icon={<MdEmojiEvents />}>
                      <Select
                        size="large"
                        showSearch
                        allowClear
                        placeholder="Select category"
                        style={{ width: "100%" }}
                        options={masterToOptions("award_category", master)}
                        value={award.award_category ?? undefined}
                        onChange={(value) => {
                          const next = _.cloneDeep(seasons);
                          next[activeSeasonIndex].awards[
                            index
                          ].award_category = value as any;
                          next[activeSeasonIndex].modified = true;
                          setSeasons(next);
                        }}
                      />
                    </AwardField>
                    <AwardField label="Status" icon={<MdMilitaryTech />}>
                      <Select
                        size="large"
                        showSearch
                        allowClear
                        placeholder="Select status"
                        style={{ width: "100%" }}
                        options={masterToOptions("festival_status", master)}
                        value={award.award_status ?? undefined}
                        onChange={(value) => {
                          const next = _.cloneDeep(seasons);
                          next[activeSeasonIndex].awards[
                            index
                          ].award_status = value as any;
                          next[activeSeasonIndex].modified = true;
                          setSeasons(next);
                        }}
                      />
                    </AwardField>
                  </AwardGrid>
                </AwardCard>
              ))}
            </AwardsList>
          )}
          <AddAwardButton
            type="dashed"
            block
            icon={<MdAdd />}
            onClick={() => {
              const next = _.cloneDeep(seasons);
              next[activeSeasonIndex].awards = [
                ...(next[activeSeasonIndex].awards ?? []),
                {
                  name_of_festival: "",
                  country: "",
                  award_year: dayjs(),
                  award_category: "",
                  award_status: "",
                  award_winner_type: "",
                } as any,
              ];
              next[activeSeasonIndex].modified = true;
              setSeasons(next);
            }}
          >
            Add award
          </AddAwardButton>
        </Section>
      )}

      <Modal
        title="Delete this season?"
        open={deleteConfirmVisible}
        onOk={confirmDeleteSeason}
        onCancel={() => {
          setDeleteConfirmVisible(false);
          setSeasonToDelete(null);
        }}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <p>
          Are you sure you want to delete Season{" "}
          {seasonToDelete !== null ? seasonToDelete + 1 : ""}? Every episode in
          this season will also be removed. This action cannot be undone.
        </p>
      </Modal>
    </Wrapper>
  );
};

const UploadField = ({
  label,
  aspect,
  help,
  required,
  error,
  children,
}: {
  label: string;
  aspect?: string;
  help?: string;
  required?: boolean;
  error?: string;
  children: ReactNode;
}) => (
  <UploadFieldShell>
    <FieldHead>
      <FieldLabel>
        <MdPhotoLibrary />
        <span>
          {label}
          {required ? <RequiredMark>*</RequiredMark> : null}
        </span>
      </FieldLabel>
      {aspect ? <AspectChip>{aspect}</AspectChip> : null}
    </FieldHead>
    <UploadBox>{children}</UploadBox>
    {help ? <Helper>{help}</Helper> : null}
    {error ? <ErrorText>{error}</ErrorText> : null}
  </UploadFieldShell>
);

const AwardField = ({
  label,
  icon,
  children,
}: {
  label: string;
  icon: ReactNode;
  children: ReactNode;
}) => (
  <AwardFieldShell>
    <AwardFieldLabel>
      {icon}
      <span>{label}</span>
    </AwardFieldLabel>
    {children}
  </AwardFieldShell>
);

const Wrapper = styled.div`
  width: 100%;
  flex-direction: column;
  gap: 1.6rem;

  &[style*="flex"] {
    display: flex !important;
  }
`;

const SeasonBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  padding: 1.6rem;
  background-color: ${card.bg};
  border: 1px solid ${card.border};
  border-radius: 1.2rem;
  box-shadow: ${card.shadow};
`;

const SeasonBarHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const SeasonBarTitle = styled.h3`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: ${colors.text.hex};
  letter-spacing: -0.01em;
`;

const SeasonBarHint = styled.span`
  font-size: 1.2rem;
  color: ${colors.text.hex};
  opacity: 0.55;
`;

const SeasonChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.7rem;
`;

const SeasonChip = styled.button<{ $active?: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.55rem 1.1rem 0.55rem 0.6rem;
  background: ${({ $active }) => ($active ? colors.primary.hex : card.innerBg)};
  color: ${({ $active }) =>
    $active ? colors.white.hex : colors.text.hex};
  border: 1px solid
    ${({ $active }) => ($active ? colors.primary.hex : card.border)};
  border-radius: 999px;
  cursor: pointer;
  font-size: 1.25rem;
  font-weight: 500;
  letter-spacing: -0.005em;
  transition: background 0.15s, color 0.15s, border-color 0.15s, transform 0.1s;

  &:hover {
    border-color: ${colors.primary.hex};
  }

  &:active {
    transform: translateY(0.05rem);
  }
`;

const ChipNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.2rem;
  height: 2.2rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.16);
  font-size: 1.2rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;

  ${SeasonChip}:not([class*="active-true"]) & {
    background: ${card.bg};
    color: ${colors.primary.hex};
  }
`;

const ChipLabel = styled.span`
  font-size: 1.25rem;
  font-weight: 500;
  max-width: 14rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChipRemove = styled.button`
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.18);
  color: currentColor;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.3rem;
  opacity: 0.85;
  transition: background 0.15s, opacity 0.15s;
  padding: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.32);
    opacity: 1;
  }

  ${SeasonChip}:not([class*="active-true"]) & {
    background: ${card.bg};
    color: ${colors.error.hex};
    opacity: 0.7;
  }
`;

const AddSeasonChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 1.2rem;
  background: transparent;
  color: ${colors.primary.hex};
  border: 1.5px dashed ${card.border};
  border-radius: 999px;
  cursor: pointer;
  font-size: 1.25rem;
  font-weight: 500;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  & svg {
    font-size: 1.5rem;
  }

  &:hover {
    border-color: ${colors.primary.hex};
    background: ${card.accentSoft};
  }
`;

const SegmentedTabs = styled.div`
  display: inline-flex;
  padding: 0.4rem;
  background-color: ${card.bg};
  border: 1px solid ${card.border};
  border-radius: 0.9rem;
  gap: 0.3rem;
  align-self: flex-start;
`;

const SegmentedTab = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.7rem 1.4rem;
  background: ${({ $active }) =>
    $active ? colors.primary.hex : "transparent"};
  color: ${({ $active }) =>
    $active ? colors.white.hex : colors.text.hex};
  border: none;
  border-radius: 0.6rem;
  cursor: pointer;
  font-size: 1.3rem;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  letter-spacing: -0.005em;
  transition: background 0.15s, color 0.15s;

  & svg {
    font-size: 1.55rem;
    opacity: ${({ $active }) => ($active ? 1 : 0.7)};
  }

  &:hover {
    background: ${({ $active }) =>
      $active ? colors.primary.hex : card.accentSoft};
  }
`;

const FieldError = styled.div`
  font-size: 1.15rem;
  color: ${colors.error.hex};
  line-height: 1.4;
`;

const SubHead = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${colors.text.hex};
  opacity: 0.55;
`;

const Divider = styled.div`
  height: 1px;
  background: ${card.divider};
  width: 100%;
  margin: 0.4rem 0;
`;

const PosterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.4rem 1.6rem;
  width: 100%;
  container-type: inline-size;

  @container (max-width: 56rem) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @container (max-width: 32rem) {
    grid-template-columns: 1fr;
  }
`;

const UploadFieldShell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  min-width: 0;
  width: 100%;
`;

const FieldHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
`;

const FieldLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.3rem;
  font-weight: 500;
  color: ${colors.text.hex};
  line-height: 1.2;

  svg {
    flex: 0 0 auto;
    color: ${colors.primary.hex};
    font-size: 1.5rem;
    opacity: 0.85;
  }
`;

const RequiredMark = styled.span`
  color: ${colors.error.hex};
  margin-left: 0.2rem;
`;

const AspectChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.1rem 0.7rem;
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: ${colors.primary.hex};
  background: ${card.bg};
  border: 1px solid ${card.border};
  border-radius: 999px;
`;

const TILE_SIZE = "11rem";

const UploadBox = styled.div`
  display: block;
  width: 100%;

  & .ant-upload-wrapper,
  & .ant-upload-picture-card-wrapper {
    width: 100%;
  }

  & .ant-upload-list,
  & .ant-upload-list-picture-card {
    display: flex !important;
    flex-wrap: wrap;
    gap: 0.8rem;
    align-items: flex-start;
  }

  & .ant-upload-list-item-container,
  & .ant-upload-list-picture-card .ant-upload-list-item-container {
    width: ${TILE_SIZE} !important;
    height: ${TILE_SIZE} !important;
    margin: 0 !important;
    flex: 0 0 auto;
  }

  & .ant-upload-list-item,
  & .ant-upload-list-item-done {
    width: 100% !important;
    height: 100% !important;
    padding: 0 !important;
    border: 1px solid ${card.border} !important;
    border-radius: 0.7rem !important;
    overflow: hidden;
    background: ${card.innerBg} !important;
  }

  & .ant-upload-list-item::before {
    border-radius: 0.7rem;
  }

  & .ant-upload-list-item-thumbnail,
  & .ant-upload-list-item-image,
  & .ant-upload-list-item-info {
    width: 100% !important;
    height: 100% !important;
  }

  & .ant-upload-list-item-thumbnail img,
  & .ant-upload-list-item-image img {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
    border-radius: 0;
  }

  & .ant-upload-list-item-actions {
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
  }

  & .ant-upload-list-item-actions .anticon {
    color: #fff !important;
    font-size: 1.6rem;
  }

  & .ant-upload.ant-upload-select,
  & .ant-upload-select.ant-upload-select-picture-card {
    width: ${TILE_SIZE} !important;
    height: ${TILE_SIZE} !important;
    flex: 0 0 auto;
    background: ${card.innerBg} !important;
    border: 1.5px dashed ${card.border} !important;
    border-radius: 0.7rem !important;
    transition: border-color 0.15s, background 0.15s, color 0.15s;
    margin: 0 !important;
  }

  & .ant-upload.ant-upload-select:hover,
  & .ant-upload-select.ant-upload-select-picture-card:hover {
    border-color: ${colors.primary.hex} !important;
    background: ${card.accentSoft} !important;
  }

  & .ant-upload.ant-upload-select .ant-upload,
  & .ant-upload-select.ant-upload-select-picture-card .ant-upload {
    padding: 0.6rem !important;
    color: ${colors.text.hex};
    display: flex !important;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.3rem;
  }

  & .ant-upload.ant-upload-select .ant-upload > div,
  & .ant-upload-select.ant-upload-select-picture-card .ant-upload > div {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
  }

  & .ant-upload.ant-upload-select .ant-upload > div > div,
  & .ant-upload-select.ant-upload-select-picture-card .ant-upload > div > div {
    margin-top: 0 !important;
    font-size: 1.15rem;
    font-weight: 500;
    line-height: 1.2;
    color: ${colors.text.hex};
  }

  & .ant-upload.ant-upload-select .anticon,
  & .ant-upload-select.ant-upload-select-picture-card .anticon {
    color: ${colors.primary.hex};
    font-size: 1.7rem;
  }
`;

const Helper = styled.span`
  font-size: 1.15rem;
  color: ${colors.text.hex};
  opacity: 0.55;
  line-height: 1.4;
`;

const ErrorText = styled.span`
  font-size: 1.15rem;
  color: ${colors.error.hex};
  line-height: 1.4;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 3rem 1.2rem;
  border: 1.5px dashed ${card.border};
  border-radius: 0.9rem;
  background: ${card.innerBg};
`;

const EmptyIcon = styled.div`
  width: 4.6rem;
  height: 4.6rem;
  border-radius: 50%;
  background: ${card.bg};
  color: ${colors.primary.hex};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 2.4rem;
`;

const EmptyTitle = styled.div`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${colors.text.hex};
`;

const EmptyHint = styled.div`
  font-size: 1.2rem;
  color: ${colors.text.hex};
  opacity: 0.6;
  text-align: center;
  max-width: 36rem;
`;

const AwardsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`;

const AwardCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  padding: 1.4rem;
  background: ${card.innerBg};
  border: 1px solid ${card.border};
  border-radius: 0.9rem;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: ${colors.primary.hex};
    box-shadow: 0 1px 6px rgba(32, 84, 150, 0.06);
  }
`;

const AwardHead = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

const AwardHeadGrow = styled.div`
  flex: 1;
  min-width: 0;
`;

const AwardIconBtn = styled.button<{ $danger?: boolean }>`
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: ${({ $danger }) => ($danger ? colors.error.hex : colors.text.hex)};
  opacity: 0.7;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.8rem;
  transition: background 0.15s, opacity 0.15s;

  &:hover {
    background: ${({ $danger }) =>
      $danger ? "rgba(239, 35, 60, 0.1)" : card.accentSoft};
    opacity: 1;
  }
`;

const AwardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1.2rem 1.4rem;
  width: 100%;
  container-type: inline-size;

  @container (max-width: 64rem) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @container (max-width: 32rem) {
    grid-template-columns: 1fr;
  }
`;

const AwardFieldShell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
`;

const AwardFieldLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.2rem;
  font-weight: 500;
  color: ${colors.text.hex};

  svg {
    flex: 0 0 auto;
    color: ${colors.primary.hex};
    font-size: 1.4rem;
    opacity: 0.85;
  }
`;

const AddAwardButton = styled(Button)`
  height: 4.4rem;
  border-radius: 0.8rem;
  font-weight: 500;
  font-size: 1.3rem;
  margin-top: 0.4rem;
  border-color: ${card.border};
  color: ${colors.primary.hex};

  &:hover {
    border-color: ${colors.primary.hex} !important;
    color: ${colors.primary.hex} !important;
    background: ${card.accentSoft} !important;
  }

  & svg {
    font-size: 1.6rem;
  }
`;
